import { Link, useNavigate } from "@tanstack/react-router";
import {
  AlignEndVertical,
  ChartPie,
  ListChecks,
  Users,
  NotebookText,
  FileCog,
  Settings,
  UserCog,
  BarChart2,
  GraduationCap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import {
  Brand,
  Button,
  TooltipComp,
  Typography,
  useTheme,
} from "../../../components/ui";
import "../sidebar/sidebar.scss";
import { useStore } from "../../../store/store";
import { smallLogo } from "../../../assets";
import { apiURL } from "../../../utils";
import { getAppVersion } from "@/utils/env";
import { CloseIcon } from "flowbite-react";
import {
  isAnyFeatureEnabled,
  isFeatureEnabled,
  getLastSevenDays,
} from "@/utils/commonUtils";
import {
  ADMIN_CONFIGURATIONS_ROUTES_PATH,
  ANALYTICS_ROUTES_PATH,
  CONFIGURATION_ROUTES_PATH,
  NANO_LEARNING_ROUTES_PATH,
  REPORTS_ROUTES_PATH,
  AGENT_DASHBOARD_ROUTES_PATH,
  MANAGE_TEMPLATE_ROUTES_PATH,
  EVALUTION_LIST_ROUTES_PATH,
  EMPLOYEE_MANAGEMENT_ROUTES_PATH,
} from "@/utils/sidebarRoutesUtils";
import { useActiveParent } from "@/hooks/useActiveParent";
import { usePermissions } from "@/hooks/usePermissions";
import { useAnalyticsDashboardAccess } from "@/pages/app/analytics/hooks/useAnalyticsDashboardAccess";
import { useGetVersionQuery } from "@/api/version/version.hooks";
import { resolveLandingPage } from "@/utils/routingUtils";

const CommonSidebar = () => {
  const { t } = useTranslation(["common"]);
  const isSidebarToggle = useStore((state) => state.isSidebarToggle);
  const brandingList = useStore((state) => state.brandingList);
  const { hasAnyPermission, isAgent } = usePermissions();
  const { canShowAnalyticsNav, isLoading: isAnalyticsAccessLoading } =
    useAnalyticsDashboardAccess();
  const appVersion = getAppVersion();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const setSidebarToggle = useStore((state) => state.setSidebarToggle);
  const setGlobalFilterData = useStore((state) => state.setGlobalFilterData);
  const user = useStore((state) => state.user);
  const resetGlobalFilterData = useStore(
    (state) => state.resetGlobalFilterData,
  );
  // check  Active state based on Routes
  const isAnalyticsActive = useActiveParent(ANALYTICS_ROUTES_PATH);
  const isConfigurationActive = useActiveParent(CONFIGURATION_ROUTES_PATH);
  const isAdminConfigurationActive = useActiveParent(
    ADMIN_CONFIGURATIONS_ROUTES_PATH,
  );
  const isEvaluationListActive = useActiveParent(EVALUTION_LIST_ROUTES_PATH);
  const isManageTemplateListActive = useActiveParent(
    MANAGE_TEMPLATE_ROUTES_PATH,
  );
  const isAgentPerformanceDashboardActive =
    useActiveParent(AGENT_DASHBOARD_ROUTES_PATH) && isAgent;
  const isReportActive =
    useActiveParent(REPORTS_ROUTES_PATH) && !isAgentPerformanceDashboardActive;
  const isNanoLearningActive = useActiveParent(NANO_LEARNING_ROUTES_PATH);
  const isEmployeeManagementActive = useActiveParent(
    EMPLOYEE_MANAGEMENT_ROUTES_PATH,
  );

  // Check if the current route matches any of the defined routes
  const { data: versionData } = useGetVersionQuery({
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
  });

  let logoSrc: string = smallLogo;
  const logo =
    theme === "dark"
      ? brandingList?.logo_secondary_dark
      : brandingList?.logo_secondary_light;
  if (logo && typeof logo === "string" && logo.trim() !== "") {
    logoSrc = logo.startsWith("http") ? logo : `${apiURL(logo).toString()}`;
  } else if (logo && typeof logo !== "string") {
    logoSrc = URL.createObjectURL(logo);
  }

  const resetEvaluationFilters = useCallback(() => {
    const { startDate, endDate } = getLastSevenDays();

    resetGlobalFilterData?.();
    setGlobalFilterData?.({
      startDate,
      endDate,
      dateType: "call_date",
      entity: "",
      type: "",
      campaign_id: "",
      service_id: "",
      agent_id: "",
      location: "",
      searchType: "",
      searchQuery: "",
    });
  }, [resetGlobalFilterData, setGlobalFilterData]);

  const handleLandingPage = () => {
    if (!user) {
      return;
    }
    const landingPath = resolveLandingPage(user);
    void navigate({ to: landingPath });
  };

  return (
    <>
      <nav className="flex flex-col gap-2 px-2 py-2 overflow-visible">
        {!isSidebarToggle ? (
          <Brand onClick={() => handleLandingPage()} />
        ) : (
          <div role="button" onClick={() => handleLandingPage()}>
            <img src={logoSrc} className="w-full" alt="Mini Logo" />
          </div>
        )}
        <Button
          className="sidebar-closeIcon"
          variant={"ghost"}
          onClick={() => setSidebarToggle(!isSidebarToggle)}
          id="sidebar-toggle-button"
        >
          <CloseIcon />
        </Button>
      </nav>
      <nav className="flex flex-col gap-2 px-2 py-5 menus sidebar-height">
        {isFeatureEnabled("evaluation-dashboard") &&
          hasAnyPermission(["can-view-evaluation-dashboard"]) && (
            <Link
              to="/evaluation-dashboard"
              activeProps={{ className: "sidebar-active-link" }}
              activeOptions={{ exact: true }}
              className={`flex items-center sidebar-link text-foreground text-sm ${
                !isSidebarToggle ? "" : "justify-center"
              }`}
              onClick={resetEvaluationFilters}
              id="sidebar-evaluation-dashboard-link"
            >
              {isSidebarToggle ? (
                <TooltipComp
                  content={t("SIDEBAR_LABEL.EVALUATION_DASHBOARD")}
                  placement="right"
                >
                  <ChartPie className="mx-2" size={18} />
                </TooltipComp>
              ) : (
                <ChartPie className="mx-2" size={18} />
              )}
              {/* Show label only if sidebar is expanded */}
              {!isSidebarToggle && (
                <span>{t("SIDEBAR_LABEL.EVALUATION_DASHBOARD")}</span>
              )}
            </Link>
          )}
        {isFeatureEnabled("agent-performance-dashboard") &&
          hasAnyPermission(["can-view-agent-performance-report-details"]) &&
          isAgent &&
          user?.employee?.internal_id &&
          user?.employee?.internal_id !== "undefined" && (
            <Link
              to="/agent-performance-details/$agentId"
              params={{
                agentId: user?.employee?.internal_id,
              }}
              search={{
                agentName: user?.employee
                  ? `${user.employee.first_name || ""} ${user.employee.last_name || ""}`.trim()
                  : user?.full_name || "",
              }}
              className={`flex items-center sidebar-link text-foreground text-sm ${
                !isSidebarToggle ? "" : "justify-center"
              } ${
                isAgentPerformanceDashboardActive ? "sidebar-active-link" : ""
              }`}
              activeProps={{ className: "sidebar-active-link" }}
              onClick={resetEvaluationFilters}
              id="sidebar-agent-performance-dashboard-link"
            >
              {isSidebarToggle ? (
                <TooltipComp
                  content={t("SIDEBAR_LABEL.AGENT_PERFORMANCE_DASHBOARD")}
                  placement="right"
                >
                  <ChartPie className="mx-2" size={18} />
                </TooltipComp>
              ) : (
                <ChartPie className="mx-2" size={18} />
              )}
              {/* Show label only if sidebar is expanded */}
              {!isSidebarToggle && (
                <span>{t("SIDEBAR_LABEL.AGENT_PERFORMANCE_DASHBOARD")}</span>
              )}
            </Link>
          )}
        {isFeatureEnabled("evaluation-list") &&
          hasAnyPermission(["can-view-evaluation-list"]) && (
            <Link
              to="/evaluation-listing"
              className={`flex items-center sidebar-link text-foreground text-sm ${
                !isSidebarToggle ? "" : "justify-center"
              } ${isEvaluationListActive ? "sidebar-active-link" : ""}`}
              activeProps={{ className: "sidebar-active-link" }}
              onClick={resetEvaluationFilters}
              id="sidebar-evaluation-list-link"
            >
              {isSidebarToggle ? (
                <TooltipComp
                  content={t("SIDEBAR_LABEL.EVALUATION_LIST")}
                  placement="right"
                >
                  <ListChecks className="mx-2" size={18} />
                </TooltipComp>
              ) : (
                <ListChecks className="mx-2" size={18} />
              )}
              {/* Show label only if sidebar is expanded */}
              {!isSidebarToggle && (
                <span>{t("SIDEBAR_LABEL.EVALUATION_LIST")}</span>
              )}
            </Link>
          )}
        {isFeatureEnabled("template") &&
          hasAnyPermission(["can-view-evaluation-templates"]) && (
            <Link
              to="/manage-template"
              className={`flex items-center sidebar-link text-foreground text-sm ${
                !isSidebarToggle ? "" : "justify-center"
              } ${isManageTemplateListActive ? "sidebar-active-link" : ""}`}
              activeProps={{ className: "sidebar-active-link" }}
              id="sidebar-manage-template-link"
            >
              {isSidebarToggle ? (
                <TooltipComp
                  content={t("SIDEBAR_LABEL.MANAGE_TEMPLATE")}
                  placement="right"
                >
                  <AlignEndVertical className="mx-2" size={18} />
                </TooltipComp>
              ) : (
                <AlignEndVertical className="mx-2" size={18} />
              )}
              {/* Show label only if sidebar is expanded */}
              {!isSidebarToggle && (
                <span>{t("SIDEBAR_LABEL.MANAGE_TEMPLATE")}</span>
              )}
            </Link>
          )}

        {isFeatureEnabled("users") &&
          hasAnyPermission(["can-view-manage-users"]) && (
            <Link
              to="/manage-users"
              activeProps={{ className: "sidebar-active-link" }}
              activeOptions={{ exact: true }}
              className={`flex items-center sidebar-link text-foreground text-sm ${
                !isSidebarToggle ? "" : "justify-center"
              }`}
            >
              {isSidebarToggle ? (
                <TooltipComp
                  content={t("SIDEBAR_LABEL.MANAGE_USER")}
                  placement="right"
                >
                  <Users className="mx-2" size={18} />
                </TooltipComp>
              ) : (
                <Users className="mx-2" size={18} />
              )}
              {/* Show label only if sidebar is expanded */}
              {!isSidebarToggle && (
                <span>{t("SIDEBAR_LABEL.MANAGE_USER")}</span>
              )}
            </Link>
          )}
        {isFeatureEnabled("employee-management") &&
          hasAnyPermission(["can-view-employee-list"]) && (
            <Link
              to="/employee-management"
              activeProps={{ className: "sidebar-active-link" }}
              className={`flex items-center sidebar-link text-foreground text-sm ${
                !isSidebarToggle ? "" : "justify-center"
              } ${isEmployeeManagementActive ? "sidebar-active-link" : ""}`}
            >
              {isSidebarToggle ? (
                <TooltipComp
                  content={t("SIDEBAR_LABEL.EMPLOYEE_MANAGEMENT")}
                  placement="right"
                >
                  <UserCog className="mx-2" size={18} />
                </TooltipComp>
              ) : (
                <UserCog className="mx-2" size={18} />
              )}
              {/* Show label only if sidebar is expanded */}
              {!isSidebarToggle && (
                <span>{t("SIDEBAR_LABEL.EMPLOYEE_MANAGEMENT")}</span>
              )}
            </Link>
          )}
        {/* Nano Learning Hub Section - For all users */}
        {isFeatureEnabled("nano-learning") &&
          hasAnyPermission(["can-view-nano-learning"]) && (
            <Link
              to="/nano-learning-hub"
              className={`flex items-center sidebar-link text-foreground text-sm ${
                !isSidebarToggle ? "" : "justify-center"
              } ${isNanoLearningActive ? "sidebar-active-link" : ""}`}
              activeProps={{ className: "sidebar-active-link" }}
              id="sidebar-browse-content-link"
            >
              {isSidebarToggle ? (
                <TooltipComp
                  content={t("SIDEBAR_LABEL.NANO_LEARNING_HUB")}
                  placement="right"
                >
                  <GraduationCap className="mx-2" size={18} />
                </TooltipComp>
              ) : (
                <GraduationCap className="mx-2" size={18} />
              )}

              {!isSidebarToggle && (
                <span className=" text-foreground">
                  {t("SIDEBAR_LABEL.NANO_LEARNING_HUB")}
                </span>
              )}
            </Link>
          )}

        {/* Reports */}
        {isAnyFeatureEnabled(REPORTS_ROUTES_PATH) &&
          hasAnyPermission(["can-view-reports"]) && (
            <Link
              to="/reports"
              className={`flex items-center sidebar-link text-foreground text-sm ${
                !isSidebarToggle ? "" : "justify-center"
              } ${isReportActive ? "sidebar-active-link" : ""}`}
              id="sidebar-reports-link"
            >
              {isSidebarToggle ? (
                <TooltipComp
                  content={t("SIDEBAR_LABEL.REPORTS.TITLE")}
                  placement="right"
                >
                  <NotebookText className="mx-2" size={18} />
                </TooltipComp>
              ) : (
                <NotebookText className="mx-2" size={18} />
              )}
              {!isSidebarToggle && (
                <span className=" text-foreground">
                  {t("SIDEBAR_LABEL.REPORTS.TITLE")}
                </span>
              )}
            </Link>
          )}
        {/* Analytics — module RBAC or resource-level shared/public dashboards */}
        {!isAnalyticsAccessLoading && canShowAnalyticsNav && (
          <Link
            to="/analytics/dashboards"
            className={`flex items-center sidebar-link text-foreground text-sm ${
              !isSidebarToggle ? "" : "justify-center"
            } ${isAnalyticsActive ? "sidebar-active-link" : ""}`}
            id="sidebar-analytics-link"
          >
            {isSidebarToggle ? (
              <TooltipComp
                content={t("SIDEBAR_LABEL.ANALYTICS.TITLE")}
                placement="right"
              >
                <BarChart2 className="mx-2" size={18} />
              </TooltipComp>
            ) : (
              <BarChart2 className="mx-2" size={18} />
            )}
            {!isSidebarToggle && (
              <span className="text-foreground">
                {t("SIDEBAR_LABEL.ANALYTICS.TITLE")}
              </span>
            )}
          </Link>
        )}
        {isAnyFeatureEnabled(CONFIGURATION_ROUTES_PATH) &&
          hasAnyPermission(["can-view-configurations"]) && (
            <Link
              to="/configurations"
              className={`flex items-center sidebar-link text-foreground text-sm ${
                !isSidebarToggle ? "" : "justify-center"
              } ${isConfigurationActive ? "sidebar-active-link" : ""}`}
              // activeProps={{ className: "sidebar-active-link" }}
              id="sidebar-configurations-link"
            >
              {isSidebarToggle ? (
                <TooltipComp
                  content={t("SIDEBAR_LABEL.CONFIGURATION")}
                  placement="right"
                >
                  <FileCog className="mx-2" size={16} />
                </TooltipComp>
              ) : (
                <FileCog className="mx-2" size={16} />
              )}
              {!isSidebarToggle && (
                <span className=" text-foreground">
                  {t("SIDEBAR_LABEL.CONFIGURATION")}
                </span>
              )}
            </Link>
          )}

        {/* admin Links */}
        {hasAnyPermission(["can-view-admin-configurations"]) && (
          <Link
            to="/admin/dashboard"
            // activeProps={{ className: "sidebar-active-link" }}
            // activeOptions={{ exact: true }}
            className={`flex items-center sidebar-link text-foreground text-sm ${
              !isSidebarToggle ? "" : "justify-center"
            } ${isAdminConfigurationActive ? "sidebar-active-link" : ""}`}
            id="sidebar-admin-configuration-link"
          >
            {isSidebarToggle ? (
              <TooltipComp
                content={t("SIDEBAR_LABEL.ADMIN.ADMIN_DASHBOARD")}
                placement="right"
              >
                <Settings className="mx-2" size={18} />
              </TooltipComp>
            ) : (
              <Settings className="mx-2" size={18} />
            )}
            {/* Show label only if sidebar is expanded */}
            {!isSidebarToggle && (
              <span>{t("SIDEBAR_LABEL.ADMIN.ADMIN_DASHBOARD")}</span>
            )}
          </Link>
        )}
      </nav>
      <div className="px-2 py-2 text-center">
        <Typography variant="p" affects="muted" className="text-gray">
          {!isSidebarToggle && "version "}
          {versionData?.data?.version ?? appVersion}
        </Typography>
      </div>
    </>
  );
};

export default CommonSidebar;
